import React, { useMemo, useRef, useState } from "react"

type DialogProps<Ret, Data> = {
  onResolve(data: Ret): any
  data: Data
  dialogData: Data
}

export function useConfirmation<Data, Ret>(
  Dialog: (props: DialogProps<Ret, Data>) => React.ReactNode,
): [React.ReactNode, (data?: Data) => Promise<Ret>] {
  const [state, setState] = useState({
    showDialog: false,
    dialogData: undefined as Data | undefined,
  })
  const promiseRef = useRef<IPromise<Ret>>()

  async function getConfirmation(data?: any) {
    promiseRef.current = getPromise()
    setState({ showDialog: true, dialogData: data })
    promiseRef.current.promise.then(() => {
      setState({ showDialog: false, dialogData: undefined })
    })
    return promiseRef.current.promise
  }

  interface IPromise<T> {
    promise: Promise<T>

    resolve(val: T): void

    reject(val: any): void
  }

  function getPromise<T>(): IPromise<T> {
    let resolve: any
    let reject: any
    const promise = new Promise<T>((...args) => {
      resolve = args[0]
      reject = args[1]
    })
    return { promise, resolve, reject }
  }

  function handleResolve(value: Ret) {
    promiseRef.current!.resolve(value)
  }

  function isDataIsEvent(data: any) {
    return (
      data !== null && typeof data === "object" && "bubbles" in data && "defaultPrevented" in data
    )
  }
  const memoComponent = useMemo(
    () => (
      <DialogWrapper
        showDialog={state.showDialog}
        Dialog={Dialog}
        onResolve={handleResolve}
        data={state.dialogData}
      />
    ),
    [state.showDialog, isDataIsEvent(state.dialogData) ? null : JSON.stringify(state.dialogData)],
  )
  return [memoComponent, getConfirmation]
}

function DialogWrapper<Ret, Data>({
  showDialog,
  onResolve,
  Dialog,
  data,
}: {
  showDialog?: boolean
  onResolve(data: any): any
  data: any
  Dialog: (args: DialogProps<Ret, Data>) => any
}) {
  if (!showDialog) {
    return null
  }
  return <Dialog onResolve={onResolve} data={data} dialogData={data} />
}

export function useConfirmation2<Data, Ret>(
  Dialog: (props: DialogProps<Ret, Data>) => React.ReactNode,
): [React.ReactNode, (data: Data) => Promise<Ret>] {
  const D = Dialog as any
  return useConfirmation<Data, Ret>(D)
}
